import type { Plan } from './billing-client';

/**
 * The plan's name as this locale should show it.
 *
 * `plan.name` is a single string in the database — «Бизнес», «ИП», «Старт»,
 * «Pro» — written in Russian, and it was rendered verbatim in both locales. So
 * the Uzbek pricing page read «ИП — oy» and «Бизнесga obuna boʻlish»: Cyrillic
 * plan names sitting inside otherwise fully Latin-script Uzbek sentences.
 *
 * Resolved in the web rather than by adding a `nameUz` column, because a plan's
 * name is marketing copy, not billing data. Nothing charges by it, the i18n
 * files are where every other piece of marketing copy on this page already
 * lives, and going through the database would mean a migration, a seed change
 * and a deploy to fix a typo in a label.
 *
 * Keyed on the plan-key FAMILY (`pro-monthly` → `pro`) so a plan added in a new
 * period inherits its tier's name for free, and **falls back to `plan.name`**
 * for anything unrecognised — the allowlisted test plan, or a tier introduced
 * before someone remembers to add copy. An unknown plan shows the database's
 * own label, never a raw key.
 */
export function planDisplayName(
  plan: Pick<Plan, 'key' | 'name'>,
  t: (k: string) => string,
): string {
  const family = plan.key.split('-')[0];
  const key = `plans.name.${family}`;
  const translated = t(key);
  // `t` echoes the key back when there is no copy for it — that is the signal
  // to defer to the database rather than print `plans.name.test` at a customer.
  return translated === key ? plan.name : translated;
}
